import React, { useState, useMemo } from 'react';
import { LogEntry, LogType } from '../types';

interface LogViewerProps {
  logs: LogEntry[];
  title?: string;
}

const LogViewer: React.FC<LogViewerProps> = ({ logs, title = "Execution Logs" }) => {
  // Ensure logs is always an array
  const safeLogs = Array.isArray(logs) ? logs : [];
  
  const [selectedTypes, setSelectedTypes] = useState<LogType[]>([LogType.INFO, LogType.WARNING, LogType.ERROR]);

  const logTypeLabels = {
    [LogType.INFO]: 'Info',
    [LogType.WARNING]: 'Warning', 
    [LogType.ERROR]: 'Error'
  };

  const logTypeColors = {
    [LogType.INFO]: 'text-blue-600 bg-blue-50 border-blue-200',
    [LogType.WARNING]: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    [LogType.ERROR]: 'text-red-600 bg-red-50 border-red-200'
  };

  const logTypeIcons = {
    [LogType.INFO]: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    [LogType.WARNING]: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    ),
    [LogType.ERROR]: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };

  const filteredLogs = useMemo(() => {
    return safeLogs.filter(log => selectedTypes.includes(log.Type));
  }, [safeLogs, selectedTypes]);

  const logCounts = useMemo(() => {
    const counts = { [LogType.INFO]: 0, [LogType.WARNING]: 0, [LogType.ERROR]: 0 };
    safeLogs.forEach(log => {
      counts[log.Type]++;
    });
    return counts;
  }, [safeLogs]);

  const toggleLogType = (type: LogType) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const selectAllTypes = () => {
    setSelectedTypes([LogType.INFO, LogType.WARNING, LogType.ERROR]);
  };

  const clearAllTypes = () => {
    setSelectedTypes([]);
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Total: {safeLogs.length}</span>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Filter by Log Type</h4>
          <div className="flex space-x-2">
            <button
              onClick={selectAllTypes}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              Select All
            </button>
            <button
              onClick={clearAllTypes}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {Object.entries(logTypeLabels).map(([type, label]) => {
            const logType = parseInt(type) as LogType;
            const isSelected = selectedTypes.includes(logType);
            const count = logCounts[logType];
            
            return (
              <button
                key={type}
                onClick={() => toggleLogType(logType)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                  isSelected 
                    ? logTypeColors[logType]
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {logTypeIcons[logType]}
                <span className="text-sm font-medium">{label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  isSelected 
                    ? 'bg-white bg-opacity-50' 
                    : 'bg-gray-100'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Logs Display */}
      <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-8">
            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500">No logs match the selected filters</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-l-4 ${
                  logTypeColors[log.Type].split(' ').slice(1).join(' ')
                } bg-white border-r border-t border-b border-gray-200`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`mt-0.5 ${logTypeColors[log.Type].split(' ')[0]}`}>
                      {logTypeIcons[log.Type]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                        {log.Message}
                      </p>
                      {log.StructName && (
                        <p className="text-xs text-gray-500 mt-1 font-mono">
                          Structure: {log.StructName}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      logTypeColors[log.Type]
                    }`}>
                      {logTypeLabels[log.Type]}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <span>
          Showing {filteredLogs.length} of {safeLogs.length} logs
        </span>
        <span>
          {selectedTypes.length === 0 ? 'No filters active' : 
           `Filtered by: ${selectedTypes.map(type => logTypeLabels[type]).join(', ')}`
          }
        </span>
      </div>
    </div>
  );
};

export default LogViewer;
